import { Box, Container, Paper, SxProps, Tab, Tabs, Theme, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react'
import { TabPanel } from 'src/components/TabPanel/TabPanel';
import AerospaceCorporationLogoPng from 'src/assets/png/aerospace_corporation_logo.png'
import AppleLogoPng from 'src/assets/png/Apple-Logo.png'
import UclaLogoPng from 'src/assets/png/UCLA-Symbol.png'
import { useDesktopMediaQuery } from 'src/hooks/useDesktopMediaQuery';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { FadeInSection } from 'src/components/FadeInSection/FadeInSection';

const Employer = {
  AEROSPACE: 'The Aerospace Corporation',
  APPLE: 'Apple',
  UCLA: 'UCLA',
};

const Experience: React.FC<{ id: string }> = ({ id }) => {
  const isDesktopScreenWidth = useDesktopMediaQuery();
  const theme = useTheme();

  const [employerTab, setEmployerTab] = useState(Employer.AEROSPACE);

  const LogoDisplay = (logoUrl: string, props?: SxProps<Theme>) => {
    return (
      <Paper
        sx={{
          ...props,
          // borderRadius: '100%',
          backgroundImage: `url(${logoUrl})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: '100%',
          // boxShadow: `1px 2px 4px ${theme.extendedPalette.accent.main}`,
          boxShadow: 'none',
        }}
      ></Paper>
    );
  };

  const experiences = [
    {
      employer: Employer.AEROSPACE,
      logo: LogoDisplay(AerospaceCorporationLogoPng, {
        width: '32px',
        height: '32px',
        padding: '20px 0 0 0',
        backgroundPosition: '50% 60%',
      }),
      positions: [
        {
          name: 'Software Engineer',
          accomplishments: [
            'Developed a responsive React web page (the new Story Details) from scratch, both on client and server side, for an app with massive scale (2 billion daily requests).',
            'Iteratively built web experiences for 80 million users across high-traffic pages.',
            'Collaborated with senior engineers and product management following best practices for the full software development life cycle, including coding standards, code reviews, source control management, build processes, testing, and operations.',
          ],
          duration: 'JUNE 2020 - PRESENT',
        },
        {
          name: 'Software Engineer Intern',
          accomplishments: [''],
          duration: 'JUNE 2018 - SEP 2018',
        },
      ],
    },
    {
      employer: Employer.APPLE,
      logo: LogoDisplay(AppleLogoPng, {
        width: '56px',
        height: '56px',
        backgroundPosition: '50% 45%',
      }),
      positions: [
        {
          name: 'Software Engineer Intern',
          accomplishments: [
            'Developed a responsive React web page (the new Story Details) from scratch, both on client and server side, for an app with massive scale (2 billion daily requests).',
            'Iteratively built web experiences for 80 million users across high-traffic pages.',
            'Collaborated with senior engineers and product management following best practices for the full software development life cycle, including coding standards, code reviews, source control management, build processes, testing, and operations.',
          ],
          duration: 'JUNE 2019 - SEP 2019',
        },
      ],
    },
    {
      employer: Employer.UCLA,
      logo: LogoDisplay(UclaLogoPng, {
        width: '48px',
        height: '48px',
        backgroundPosition: '50% 52%',
      }),
      positions: [
        {
          name: 'Software Engineer Researcher',
          accomplishments: [
            'Developed a responsive React web page (the new Story Details) from scratch, both on client and server side, for an app with massive scale (2 billion daily requests).',
            'Iteratively built web experiences for 80 million users across high-traffic pages.',
            'Collaborated with senior engineers and product management following best practices for the full software development life cycle, including coding standards, code reviews, source control management, build processes, testing, and operations.',
          ],
          duration: 'NOV 2018 - AUG 2019',
        },
      ],
    },
  ];

  return (
    <Box
      id={id}
      sx={{ width: '100%' }}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '64px 10vw',
          overflow: 'hidden',
        }}
      >
        <FadeInSection>
          <Typography
            variant='h6'
            sx={{
              color: theme.extendedPalette.accent.main,
            }}
          >
            Experience
          </Typography>
        </FadeInSection>
        <FadeInSection>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              variant='fullWidth'
              value={employerTab}
              onChange={(_, newValue: string) => {
                setEmployerTab(newValue);
              }}
            >
              {experiences.map((experience) => {
                return (
                  <Tab
                    key={experience.employer}
                    label={experience.logo}
                    value={experience.employer}
                  />
                );
              })}
            </Tabs>
          </Box>
        </FadeInSection>
        <FadeInSection>
          <Box>
            {experiences.map((experience) => {
              return (
                <TabPanel
                  key={experience.employer}
                  value={employerTab}
                  index={experience.employer}
                  sx={{
                    textAlign: 'start',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'start',
                    gap: '16px',
                    padding: '16px 0 0 0',
                  }}
                >
                  <Box>
                    <Typography
                      variant='h5'
                      component={'span'}
                      sx={{}}
                    >
                      {`${experience.positions[0].name} @ `}
                    </Typography>
                    <Typography
                      variant='h5'
                      component={'span'}
                      sx={{ color: theme.extendedPalette.accent.main }}
                    >
                      {`${experience.employer}`}
                    </Typography>
                  </Box>
                  <Typography
                    variant='body1'
                    sx={{}}
                  >
                    {`${experience.positions[0].duration}`}
                  </Typography>
                  <Box>
                    {experience.positions[0].accomplishments.map(
                      (accomplishment, index) => {
                        return (
                          <FadeInSection
                            key={index}
                            delay={(index + 1) * 100}
                            fromDirection='left'
                          >
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                justifyContent: 'start',
                                gap: '8px',
                              }}
                            >
                              <PlayArrowIcon
                                fontSize='small'
                                sx={{
                                  color: theme.extendedPalette.accent.main,
                                }}
                              />
                              <Typography
                                variant='body1'
                                sx={{ textAlign: 'start' }}
                              >
                                {accomplishment}
                              </Typography>
                            </Box>
                          </FadeInSection>
                        );
                      },
                    )}
                  </Box>
                </TabPanel>
              );
            })}
          </Box>
        </FadeInSection>
      </Container>
    </Box>
  );
};

export default Experience